import {
  FindALabSearchEvent,
  FindALabSearchEventCore,
  SearchMethod,
} from '@patient-ui/patient-web/store';

/** Meaningful lat/lon precision for matching Locate Me coordinates (~10m). */
const LAT_LNG_DIGITS = 4;

const MS_PER_DAY = 86_400_000;

export const ZIP_CODE_PATTERN = /^\d{5}$/;

/** GPS coordinates captured from a Locate Me action. */
export interface LocateMeLocation {
  latitude: number;
  longitude: number;
}

/** Location + service inputs at the point of search. */
export interface FindALabSearchEventInput {
  serviceId: number | null;
  addressZip: string;
  zipCode?: string;
  latitude?: number | null;
  longitude?: number | null;
}

/** Current Find a Lab filter state captured alongside a search. */
export interface FindALabSearchFilters {
  radius?: number;
  appointmentTimes?: string;
  openNow?: boolean;
  openWeekends?: boolean;
  isWalgreens?: boolean;
}

/** Route/query-param snapshot for Find a Lab Results sync and tracking. */
export interface FindALabSearchParamsKey {
  address?: string;
  lat?: number;
  lon?: number;
  zip?: string;
  serviceId?: number;
  favoriteLocationId?: number | null;
  date: string;
  radius: number;
  appointmentTimes: string;
  openNow: boolean;
  openWeekends: boolean;
  isWalgreens: boolean;
}

export interface FindALabSearchParamsInput {
  searchInput: FindALabSearchEventInput;
  filters: FindALabSearchFilters;
  searchMethod: SearchMethod;
  preferredDate: string;
  todayDateString: string;
}

function parseDateOnly(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** ISO slot datetime → `YYYY-MM-DD`. */
export function slotDateString(slot: string): string {
  return slot.split('T')[0];
}

/** Whole-day difference from `from` to `to` (`YYYY-MM-DD`). */
export function computeDaysBetween(from: string, to: string): number {
  const fromDate = parseDateOnly(from);
  const toDate = parseDateOnly(to);
  return Math.round((toDate.getTime() - fromDate.getTime()) / MS_PER_DAY);
}

export function computePreferredDateMetrics(
  preferredDate: string,
  todayDateString: string
): { is_same_day_search: boolean; days_until_preferred_date: number } {
  const daysUntil = computeDaysBetween(todayDateString, preferredDate);
  return {
    is_same_day_search: daysUntil === 0,
    days_until_preferred_date: daysUntil,
  };
}

/** Stable key so each distinct Results load fires the search event once. */
export function serializeFindALabSearchParamsKey(
  params: FindALabSearchParamsKey
): string {
  return JSON.stringify(params);
}

/** Returns true when the serialized params match the prior search event. */
export function isDuplicateFindALabSearchParamsKey(
  paramsKey: string,
  lastParamsKey: string | null
): boolean {
  return lastParamsKey === paramsKey;
}

/**
 * Determines how the user located labs at the point of search.
 */
export function deriveSearchMethod(
  addressZip: string,
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  lastLocateMe: LocateMeLocation | null
): SearchMethod {
  if (
    lastLocateMe != null &&
    latitude != null &&
    longitude != null &&
    latitude !== 0 &&
    longitude !== 0 &&
    latitude.toFixed(LAT_LNG_DIGITS) ===
      lastLocateMe.latitude.toFixed(LAT_LNG_DIGITS) &&
    longitude.toFixed(LAT_LNG_DIGITS) ===
      lastLocateMe.longitude.toFixed(LAT_LNG_DIGITS)
  ) {
    return 'locate_me';
  }

  if (ZIP_CODE_PATTERN.test((addressZip || '').trim())) {
    return 'zip_code';
  }

  return 'address';
}

/**
 * Resolves the ZIP Code for a location search: the explicit `zipCode` field
 * when present, otherwise the free-form `addressZip` when it looks like a ZIP.
 */
export function resolveSearchZip(
  input: FindALabSearchEventInput
): string | undefined {
  const zipCode = (input.zipCode || '').trim();
  if (zipCode) {
    return zipCode.length > 5 ? zipCode.substring(0, 5) : zipCode;
  }

  const addressZip = (input.addressZip || '').trim();
  return ZIP_CODE_PATTERN.test(addressZip) ? addressZip : undefined;
}

function parseCoordinate(value: number | null | undefined): number | undefined {
  if (value == null || value === 0) {
    return undefined;
  }
  return Number.isNaN(value) ? undefined : value;
}

/**
 * Builds the core {@link FindALabSearchEvent} payload from search inputs and filters.
 */
export function buildFindALabSearchEvent(
  input: FindALabSearchEventInput,
  filters: FindALabSearchFilters,
  searchMethod: SearchMethod
): FindALabSearchEventCore {
  const addressSingle = (input.addressZip || '').trim();
  const zip = resolveSearchZip(input);
  const lat = parseCoordinate(input.latitude);
  const lon = parseCoordinate(input.longitude);

  return {
    search_method: searchMethod,
    ...(addressSingle && { address_single: addressSingle }),
    ...(zip && { zip }),
    ...(lat != null && { lat }),
    ...(lon != null && { lon }),
    ...(input.serviceId != null && { service: input.serviceId }),
    radius: filters.radius,
    time_of_day: filters.appointmentTimes,
    open_now: Boolean(filters.openNow),
    open_weekends: Boolean(filters.openWeekends),
    labcorp_at_walgreens: Boolean(filters.isWalgreens),
  };
}

/**
 * Builds the search Track Event from Results page params (including date metrics).
 */
export function buildFindALabSearchEventFromParams(
  input: FindALabSearchParamsInput
): FindALabSearchEvent {
  return {
    ...buildFindALabSearchEvent(
      input.searchInput,
      input.filters,
      input.searchMethod
    ),
    preferred_date: input.preferredDate,
    ...computePreferredDateMetrics(input.preferredDate, input.todayDateString),
  };
}
